// İSG uzmanı portalı — İş İzni listesi (api-contract §14.1, tenant-scoped).
// `GET /permits` (sayfalı + filtreli + arama). Tablo: görev türü, başlık, **taşeron + sahalar**
// (tek sütunda İSTİFLİ — owner review B2 item 1, aşağıya bkz.; sahalar çoklu, §14 Faz 2, ilk N +
// "+X"), geçerlilik, **durum rozeti** (türetilmiş Expired dahil), personel sayısı. Filtreler:
// durum, taşeron, **saha (tek saha — iznin herhangi sahasıyla eşleşme, matches-any §14.1)**,
// görev türü, tarih aralığı (from/to + geçerlilik sonu aralığı, birincil filtrelerin ALTINDA
// katlanır — owner review B2 item 2, aşağıya bkz.), arama. "Yeni İzin" birincil butonu; her
// satırda Detay (→ /permits/{uuid}). Taşeron/saha seçenekleri ilgili tenant uçlarından; sıra
// backend'de geçerlilik başlangıcı azalan (istemci sort göndermez).

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  HazardTag,
  Input,
  PageHeader,
  Pagination,
  type PermitStatus,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  type WorkPermitDto,
  contractorsApi,
  currentCulture,
  permitTypesApi,
  permitsApi,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, Plus, SearchX, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MoreFiltersToggle } from '../../components/MoreFiltersToggle';
import { PermitFormModal } from '../../components/permits/PermitFormModal';
import { PERMIT_STATUSES, getPermitStatusPresentation } from '../../components/permits/permitEnums';
import { formatDate } from '../../lib/format';
import { permitTypesKeys } from '../../lib/permitTypesKeys';
import { permitsKeys } from '../../lib/permitsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;
/** Taşeron/saha seçicilerini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;
/** Liste saha kolonunda satır içinde gösterilecek saha adı sayısı (kalanı "+X"). */
const MAX_SITES_INLINE = 2;

/**
 * İzin satırında sahaları özetler (**çoklu lokasyon — §14 Faz 2**): ilk N ad virgüllü + kalan
 * "+X"; saha yoksa tire. Ada göre sıralı gelir (backend). Detayda tüm sahalar rozet olarak görünür.
 * `block truncate` + `title` (S33 Slice 4 QA fix, 3. tur — 1280'de tablo tile'ına SIĞMA): eski
 * `flex-wrap` sürümü sığmayınca satırı ikinci bir satıra SARIYORDU (satır yüksekliği tutarsız);
 * artık tek satır, taşan kısım (adlar veya "+X") elips ile kesilir, tam liste `title`'da.
 */
function SitesSummary({ sites }: { sites: WorkPermitDto['sites'] }) {
  const { t } = useTranslation();
  if (sites.length === 0) {
    return <>—</>;
  }
  const shown = sites.slice(0, MAX_SITES_INLINE);
  const extra = sites.length - shown.length;
  return (
    <span className="block truncate" title={sites.map((s) => s.name).join(', ')}>
      {shown.map((s) => s.name).join(', ')}
      {extra > 0 ? (
        <span className="ml-1 text-xs font-medium text-muted">
          {t('permits.list.sitesMore', { count: extra })}
        </span>
      ) : null}
    </span>
  );
}

/**
 * İzin satırında taşeron adı + sahaları TEK sütunda istifler (owner review B2 item 1,
 * 2026-09-28 — ayrı Taşeron/Sahalar sütunları 1366×768'de (256px genişletilmiş kenar çubuğu)
 * tabloyu 1100px tabana zorlayıp Personel sütununü kırpıyordu, bkz. `Table`'ın dosya başı
 * notu). Taşeron adı üstte (`line-clamp-2`, uzun tüzel adlar için), sahalar altta KÜÇÜK ve
 * SESSİZ (`text-xs`) — Başlık hücresinin kendi HazardTag alt satırıyla AYNI desen (birincil
 * bilgi üstte, ikincil altta, ikisi de kendi genişliğinde sarabilir/kırpılabilir).
 */
function ContractorAndSites({
  contractorName,
  sites,
}: {
  contractorName: string | null;
  sites: WorkPermitDto['sites'];
}) {
  return (
    <>
      <span className="line-clamp-2 block" title={contractorName ?? undefined}>
        {contractorName ?? '—'}
      </span>
      <span className="mt-0.5 block text-xs">
        <SitesSummary sites={sites} />
      </span>
    </>
  );
}

/**
 * İzin satırında geçerlilik aralığını İKİ SATIRDA gösterir (başlangıç / "– bitiş") — S34-12
 * QA fix A1: 168px sabit sütun, `formatDateRange`'in tek satırlık ortak-bileşen biçimini
 * ("27 Ağu – 26 Eki 2026") her genişlikte (1280–2560, tr/en; de için de "27.08. – 26.10.2026"
 * daha uzun) KIRPIYORDU. Tek bir tarih hiçbir kültürde bu sütuna sığmayacak kadar uzun DEĞİL
 * (en "Aug 27, 2026" ~12 karakter en uzunu) — aralığı iki satıra bölmek `formatDateRange`'in
 * kısaltılmış tek-satır biçimine ihtiyaç BIRAKMAZ, her satır kendi `formatDate`'i. Satır
 * `carbon:h-auto`+`carbon:min-h-[…]` (başlık/taşeron hücreleriyle aynı örnek) büyümeyi zaten
 * barındırıyor. Tarih eksikse (başlangıç/bitiş null) tek tire, sarma YOK.
 */
function ValidityRange({
  start,
  end,
  culture,
}: {
  start: string | null | undefined;
  end: string | null | undefined;
  culture: string;
}) {
  if (!start || !end) {
    return <>—</>;
  }
  return (
    <>
      <span className="block truncate">{formatDate(start, culture)}</span>
      <span className="block truncate">{`– ${formatDate(end, culture)}`}</span>
    </>
  );
}

/** Yerel gün başlangıcını UTC ISO'ya çevirir (from filtresi — §4 wire). */
function dayStartIso(dateStr: string): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Yerel gün sonunu UTC ISO'ya çevirir (to filtresi — §4 wire). */
function dayEndIso(dateStr: string): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const d = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function PermitsListPage() {
  const { t } = useTranslation();
  const culture = currentCulture();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  // Durum filtresi URL'in `?status=` parametresiyle İKİ YÖNLÜ senkron (BACKLOG.md "Work permits
  // Pending/Active/All alt-öğeleri" kapatma reçetesi, madde a) — SideNav'ın üç alt-öğesi
  // (Bekleyen onay / Yürürlükte / Tümü) bu sayfaya AYNI rotayla farklı `status` değerleriyle
  // gelir; sayfa zaten AÇIKKEN bir alt-öğeye tıklamak yeniden marka GEREKMEZ, `useSearchParams`
  // reaktif olduğundan filtre kendiliğinden güncellenir.
  const [searchParams, setSearchParams] = useSearchParams();
  // §34.4 YENİ — `status` artık TEKRARLANABİLİR (`?status=Active&status=AwaitingEntryPermit`,
  // panonun "süresi yaklaşan izinler" karosunun drill-through'u, M10). Tanınmayan değerler
  // backend'in kendi kuralını (§34.4 "tek tek atlanır") YANSITIR — burada da SESSİZCE elenir,
  // aksi hâlde bir bozuk/eski bağlantı okunaksız bir ham dize gösterirdi.
  const rawStatusValues = searchParams.getAll('status');
  const statusValues = rawStatusValues.filter((v): v is PermitStatus =>
    (PERMIT_STATUSES as readonly string[]).includes(v),
  );
  // Elle seçim (tekil `SelectMenu`) YALNIZ 0/1 değerde anlamlıdır — 2+ değer panonun linkinden
  // gelir, aşağıdaki salt-okunur özet onu gösterir (bkz. JSX, "çoklu durum özeti").
  const status = statusValues.length <= 1 ? (statusValues[0] ?? '') : '';
  function setStatus(value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('status');
        if (value) {
          next.set('status', value);
        }
        // `page` de AYNI güncellemede düşer (S33 Slice 7 QA-recheck fix) — SideNav'ın üç
        // alt-öğesi bu sayfaya AYNI rotayla farklı `status` değerleriyle geldiğinde eski
        // sayfa numarası HAYATTA kalıp `listPermits({status, page:3})`ı boş/yanlış
        // döndürüyordu; `page` artık local `useState` DEĞİL, bu AYNI `searchParams`'ta.
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }
  /** Panodan gelen çoklu-durum + süre penceresi süzgecini TEK ADIMDA temizler (aşağıdaki
   * salt-okunur özetin "temizle" düğmesi) — elle seçime geri döner. */
  function clearStatusFilter() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('status');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
    setValidityEndFrom('');
    setValidityEndTo('');
  }
  // Satırdan izin detayına giderken bırakılan "geldiği liste" izi (S33 Slice 4 QA fix, OWNER
  // DECISION §5) — ucuz: yalnız MEVCUT `status` değer(ler)inden türetilir, ek istek YOK. Detay
  // sayfası bunu `location.state` üzerinden okur (bkz. `PermitDetailPage.tsx`); doğrudan
  // gelinirse (yer imi, F5) `state` zaten `null` olur, jenerik "Çalışma İzinleri" kalır.
  const backState =
    statusValues.length === 1 && statusValues[0] === 'Submitted'
      ? { backHref: '/permits?status=Submitted', backLabel: t('permits.nav.pendingApproval') }
      : statusValues.length === 1 && statusValues[0] === 'Active'
        ? { backHref: '/permits?status=Active', backLabel: t('permits.nav.active') }
        : statusValues.length > 0
          ? {
              backHref: `/permits?${statusValues.map((s) => `status=${s}`).join('&')}`,
              backLabel: t('permits.title'),
            }
          : { backHref: '/permits', backLabel: t('permits.nav.all') };
  const [contractorUuid, setContractorUuid] = useState('');
  // Panonun saha karoları/aksiyon satırları `&siteUuid=` ile buraya drill-through yapar (S35
  // owner review B3-1, `DashboardPage.tsx` + api-contract §35.2) — `validityEndFrom`/`To` ile
  // AYNI desen: yalnız İLK render'da URL'den okunur (elle seçimden sonra `searchParams`'a geri
  // YAZILMAZ, `status`'un iki-yönlü senkronundan FARKLI — saha filtresi zaten kendi `Select`iyle
  // yerel `useState`), aksi hâlde panonun eklediği parametre bu SelectMenu hiç dinlemediği için
  // sessizce YOK SAYILIRDI.
  const [siteUuid, setSiteUuid] = useState(() => searchParams.get('siteUuid') ?? '');
  const [permitTypeUuid, setPermitTypeUuid] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // §34.4 YENİ — `validityEndFrom`/`validityEndTo` (yalnız `validityEnd` üzerinde aralık; §34.1
  // `expiringWindow`'un drill-through hedefi, `from`/`to`'nun PENCERE-kesişimiyle KARIŞTIRILMAZ).
  // `from`/`to` gibi salt YEREL durum (URL'e geri YAZILMAZ) — TEK fark, panodan gelen ilk
  // değerin URL'den OKUNMASI: state DOĞRUDAN sunucunun verdiği tam ISO anını taşır (gün
  // hassasiyetine YUVARLANMAZ), aksi hâlde karo `expiringIn7Days` ile bu listenin
  // `filteredTotal`ı arasındaki "AYNI sayı" vaadi (M10) bir gün-sınırı YUVARLAMASIYLA bozulurdu.
  // Kullanıcı tarihi ELLE değiştirirse `dayStartIso`/`dayEndIso` (aşağı bkz.) normal gün sınırına
  // döner — bu artık kasıtlı bir kullanıcı eylemidir.
  const [validityEndFrom, setValidityEndFrom] = useState(
    () => searchParams.get('validityEndFrom') ?? '',
  );
  const [validityEndTo, setValidityEndTo] = useState(() => searchParams.get('validityEndTo') ?? '');
  // Owner review B2 item 2 — 4 tarih filtresi (`from`/`to`/`validityEndFrom`/`validityEndTo`)
  // "Daha fazla filtre" düğmesinin ARKASINA katlanır (bkz. aşağıdaki `filters`). Panonun süresi
  // yaklaşan izinler karosu yalnız `validityEndFrom`/`validityEndTo`'yu URL'den doldurur (dosya
  // başlığı notu, M10) — bu drill-through'ta grup baştan AÇIK gelir, aksi hâlde kullanıcı hangi
  // filtrenin uygulandığını GÖRMEDEN bir sonuç listesiyle karşılaşırdı.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(
    () =>
      Boolean(searchParams.get('validityEndFrom')) || Boolean(searchParams.get('validityEndTo')),
  );
  const [createOpen, setCreateOpen] = useState(false);
  // `page` de `searchParams`'ta (yukarıdaki `status` gibi) — geri/ileri onu geri getirir,
  // VE herhangi bir tam gezinme (SideNav/Dashboard `?status=` linkleri, hiçbiri `page`
  // TAŞIMAZ) doğal olarak 1'e düşürür; local `useState` KALSAYDI eski değer hayatta kalırdı.
  const page = Number(searchParams.get('page')) || 1;
  function setPage(value: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value > 1) {
          next.set('page', String(value));
        } else {
          next.delete('page');
        }
        return next;
      },
      { replace: true },
    );
  }

  // Filtre seçicileri — taşeron (§13) + saha (§10). Bu uçlar kendi grant'larını gerektirir;
  // yetki yoksa liste boş döner (yalnız "tümü" seçeneği görünür) — filtre bozulmaz.
  const contractorsQuery = useQuery({
    queryKey: ['permits', 'options', 'contractors'],
    queryFn: () => contractorsApi.listContractors({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    staleTime: 60_000,
  });
  const sitesQuery = useQuery({
    queryKey: ['permits', 'options', 'sites'],
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });
  // İzin tipi filtresi — tüm tipler (aktif + pasif) listelenir ki eski/pasif tipin izinleri de
  // filtrelenebilsin (§22 `GET /permit-types`; `permit-types` yetkisi yoksa boş → filtre bozulmaz).
  const permitTypesQuery = useQuery({
    queryKey: permitTypesKeys.list(),
    queryFn: () => permitTypesApi.listPermitTypes({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    staleTime: 60_000,
  });

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permits.list.filter.allStatuses') },
      ...PERMIT_STATUSES.map((s) => ({
        value: s,
        label: t(getPermitStatusPresentation(s).labelKey),
      })),
    ],
    [t],
  );
  const permitTypeOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permits.list.filter.allPermitTypes') },
      ...(permitTypesQuery.data?.items ?? []).map((pt) => ({ value: pt.uuid, label: pt.name })),
    ],
    [t, permitTypesQuery.data],
  );
  const contractorOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permits.list.filter.allContractors') },
      ...(contractorsQuery.data?.items ?? []).map((c) => ({ value: c.uuid, label: c.name })),
    ],
    [t, contractorsQuery.data],
  );
  const siteOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permits.list.filter.allSites') },
      ...(sitesQuery.data?.items ?? []).map((s) => ({
        value: s.uuid,
        label: s.branchName ? `${s.name} · ${s.branchName}` : s.name,
      })),
    ],
    [t, sitesQuery.data],
  );

  const params = {
    status: statusValues.length > 0 ? statusValues : undefined,
    contractorUuid: contractorUuid || undefined,
    siteUuid: siteUuid || undefined,
    permitTypeUuid: permitTypeUuid || undefined,
    from: dayStartIso(from),
    to: dayEndIso(to),
    validityEndFrom: validityEndFrom || undefined,
    validityEndTo: validityEndTo || undefined,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: permitsKeys.listParams(params),
    queryFn: () => permitsApi.listPermits(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  // S34-2 — boş liste "veri yok" (oluştur aksiyonu) ile "sonuç yok" (filtre temizle) arasında
  // ayrım yapar (design review §2, M13). `status` de bir filtre SAYILIR — SideNav'ın Pending/
  // Active alt-öğesinden gelinse bile "Filtreleri temizle" tüm izinleri gösterir.
  const hasActiveFilters = Boolean(
    debouncedSearch ||
      statusValues.length > 0 ||
      contractorUuid ||
      siteUuid ||
      permitTypeUuid ||
      from ||
      to ||
      validityEndFrom ||
      validityEndTo,
  );
  // Owner review B2 item 2 — "Daha fazla filtre" rozeti; katlı 4 tarih filtresinden kaçı DOLU.
  const moreFiltersActiveCount = [from, to, validityEndFrom, validityEndTo].filter(Boolean).length;
  const [density, setDensity] = useTableDensity();
  function clearFilters() {
    setSearch('');
    setContractorUuid('');
    setSiteUuid('');
    setPermitTypeUuid('');
    setFrom('');
    setTo('');
    setValidityEndFrom('');
    setValidityEndTo('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('status');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('permits.title') }]} />
      <PageHeader
        title={t('permits.title')}
        helperText={t('permits.list.subtitle')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('permits.list.new')}
          </Button>
        }
      />

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data ? t('permits.list.count', { count: query.data.filteredTotal }) : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('permits.list.search')}
            searchPlaceholder={t('permits.list.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <>
                {/* HER filtre `TableToolbarFilter`e AYRI AYRI sarmalı (S33 Slice 4 QA fix,
                    3. tur) — `filters` burada bir `<>` fragment'i, TableToolbar artık BUNU
                    içeriden AÇMIYOR (bkz. TableToolbar.tsx dosya başlığı).
                    **Görünür etiket HER filtrede (S33-8 review fix #7).** Eskiden select'ler
                    yalnız `ariaLabel` taşıyordu (görünmez), tarih alanları `Field`le görünür
                    etiketliydi — aynı satırda farklı yükseklikte iki kutu türü sarmayı
                    çarpıklaştırıyordu. Artık HEPSİ `Field`e bağlı (`SelectMenu`'nün yeni `id`
                    prop'u), aynı i18n anahtarı hem etiket hem de (ariaLabel yerine) erişilebilir
                    ad. */}
                {statusValues.length > 1 ? (
                  // Panodan gelen çoklu-durum drill-through (§34.4, M10) — tekil `SelectMenu`
                  // bunu TEMSİL EDEMEZ (bir arayüz değeri = bir statü). Salt-okunur bir özet +
                  // "temizle" düğmesi elle seçime geri döner.
                  <TableToolbarFilter>
                    <div className="flex flex-col gap-1.5">
                      <span className="text-sm font-medium text-fg carbon:text-xs carbon:tracking-[.32px] carbon:text-muted">
                        {t('permits.list.filter.status')}
                      </span>
                      <div className="flex h-10 flex-wrap items-center gap-1.5">
                        {statusValues.map((s) => (
                          <Tag key={s} color={getPermitStatusPresentation(s).tagColor}>
                            {t(getPermitStatusPresentation(s).labelKey)}
                          </Tag>
                        ))}
                        <button
                          type="button"
                          onClick={clearStatusFilter}
                          className="inline-flex items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('common.clearFilters')}
                        </button>
                      </div>
                    </div>
                  </TableToolbarFilter>
                ) : (
                  <TableToolbarFilter>
                    <Field htmlFor="permits-filter-status" label={t('permits.list.filter.status')}>
                      <SelectMenu
                        id="permits-filter-status"
                        placeholder={t('permits.list.filter.allStatuses')}
                        options={statusOptions}
                        value={status}
                        onValueChange={setStatus}
                      />
                    </Field>
                  </TableToolbarFilter>
                )}
                <TableToolbarFilter>
                  <Field
                    htmlFor="permits-filter-contractor"
                    label={t('permits.list.filter.contractor')}
                  >
                    <SelectMenu
                      id="permits-filter-contractor"
                      placeholder={t('permits.list.filter.allContractors')}
                      options={contractorOptions}
                      value={contractorUuid}
                      onValueChange={resetPage(setContractorUuid)}
                    />
                  </Field>
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <Field htmlFor="permits-filter-site" label={t('permits.list.filter.site')}>
                    <SelectMenu
                      id="permits-filter-site"
                      placeholder={t('permits.list.filter.allSites')}
                      options={siteOptions}
                      value={siteUuid}
                      onValueChange={resetPage(setSiteUuid)}
                    />
                  </Field>
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <Field
                    htmlFor="permits-filter-permit-type"
                    label={t('permits.list.filter.permitType')}
                  >
                    <SelectMenu
                      id="permits-filter-permit-type"
                      placeholder={t('permits.list.filter.allPermitTypes')}
                      options={permitTypeOptions}
                      value={permitTypeUuid}
                      onValueChange={resetPage(setPermitTypeUuid)}
                    />
                  </Field>
                </TableToolbarFilter>
                {/* Owner review B2 item 2 — 4 tarih filtresi (aşağıda) bu düğmenin ARKASINA
                    katlanır; birincil 4 filtre (üstte) her zaman tek satırda kalır. */}
                <MoreFiltersToggle
                  open={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen((v) => !v)}
                  activeCount={moreFiltersActiveCount}
                  label={t('common.moreFilters')}
                />
                {moreFiltersOpen ? (
                  // S36 #16b — bu 4 tarih filtresi ÜSTTEKİ birincil 4 filtre + "Daha fazla
                  // filtre" düğmesiyle AYNI `flex-wrap` akışına (`TableToolbar.tsx`deki
                  // `{filters}` satırı) düz kardeş olarak katılıyordu; üsttekiler o satırı tam
                  // doldurmadığı genişliklerde tarihler ARTAN boşluğa devam ediyor, kendi
                  // satırına BAŞLAMIYORDU (hizasız kuyruk). `w-full` bir sarmalayıcı — flex akışında
                  // %100 genişlikli tek bir öğe asla bir öncekiyle AYNI satırı PAYLAŞAMAZ — dört
                  // tarihi KENDİ satırına zorlar, tek satırda hizalı kalmaları için sarmalayıcının
                  // KENDİSİ de `flex-wrap` (dar viewport'ta 4'ü aralarında sarabilsin, sayfanın
                  // geneli TAŞMASIN).
                  <div className="flex w-full flex-wrap items-end gap-x-4 gap-y-2">
                    <TableToolbarFilter>
                      <Field htmlFor="permits-filter-from" label={t('permits.list.filter.from')}>
                        <Input
                          id="permits-filter-from"
                          type="date"
                          value={from}
                          onChange={(e) => resetPage(setFrom)(e.target.value)}
                        />
                      </Field>
                    </TableToolbarFilter>
                    <TableToolbarFilter>
                      <Field htmlFor="permits-filter-to" label={t('permits.list.filter.to')}>
                        <Input
                          id="permits-filter-to"
                          type="date"
                          value={to}
                          onChange={(e) => resetPage(setTo)(e.target.value)}
                        />
                      </Field>
                    </TableToolbarFilter>
                    <TableToolbarFilter>
                      <Field
                        htmlFor="permits-filter-validity-end-from"
                        label={t('permits.list.filter.validityEndFrom')}
                      >
                        <Input
                          id="permits-filter-validity-end-from"
                          type="date"
                          value={validityEndFrom ? validityEndFrom.slice(0, 10) : ''}
                          onChange={(e) =>
                            resetPage(setValidityEndFrom)(dayStartIso(e.target.value) ?? '')
                          }
                        />
                      </Field>
                    </TableToolbarFilter>
                    <TableToolbarFilter>
                      <Field
                        htmlFor="permits-filter-validity-end-to"
                        label={t('permits.list.filter.validityEndTo')}
                      >
                        <Input
                          id="permits-filter-validity-end-to"
                          type="date"
                          value={validityEndTo ? validityEndTo.slice(0, 10) : ''}
                          onChange={(e) =>
                            resetPage(setValidityEndTo)(dayEndIso(e.target.value) ?? '')
                          }
                        />
                      </Field>
                    </TableToolbarFilter>
                  </div>
                ) : null}
              </>
            }
          />

          {/* `table-fixed` + her `<th>`de EXPLICIT genişlik (S33 Slice 4 QA fix, 3. tur — coordinator
              ölçümü: 1280'de tile 960px, `table-layout:auto` altında sütun toplamı 1139px'e
              içerik-güdümlü BÜYÜYORDU → 179px iç kaydırma). Emsal `DayListTab.tsx` (S32e §5/§6):
              genişlik SATIRLARDAN değil `<th>`lerden gelir. `min-w-[…]` `<table>`de (S33 Slice 7
              QA fix, HIGH) — `width:100%` tek başına GENİŞLİĞİ kapsayıcıya sabitler; dar
              kapsayıcılarda (1024) `table-fixed`in "kalan alan" hesabı Başlık'ı 0px'e çökertirdi.
              **S34-1 A1 (web-ui-designer review §5.1, M14) — "bounded" sütunlar px/%, Başlık
              REMAINDER.** Doğru bölüşüm: **sınırlı içerik → sabit px**, **serbest metin → %**
              (kapsayıcı büyüdükçe BÜYÜR), **Başlık → genişlik YOK** (`table-layout:fixed`de
              genişliği belirtilmemiş TEK sütun, kalan TÜM alanı alır — zaten 2 satıra
              sarabiliyor, `line-clamp-2` aşağıda).
              **Owner review B2 item 1 (2026-09-28) — 1366×768'te (256px genişletilmiş kenar
              çubuğu, `main` kendisi 1046px) tablo YATAY KAYIYOR, Personel sütunü KIRPILIYORDU**
              (eski `min-w-[1100px]` tabanı bu genişliğin 54px ÜSTÜNDEYDİ). İki değişiklik:
              **(a) Taşeron + Sahalar TEK sütuna istiflendi** (`ContractorAndSites`, ayrı iki
              sütunun toplam genişliği yerine tek bir %18 — iki ayrı hücrenin dolgu/kenarlık
              yükünü de kaldırır); **(b) Geçerlilik 168px'ten 144px'e daraltıldı** (içerik iki
              KISA tarih satırı, `Aug 27, 2026` en uzun kültürde bile ~92px — 144px bolca pay
              bırakır) VE **(c) Durum 200px'ten 256px'e BÜYÜTÜLDÜ** — eski 200px, DE'nin en uzun
              statü etiketini (`AwaitingSupplierInfo`/`AwaitingEntryPermit`, 31 karakter,
              "Warten auf …") aslında KIRPIYORDU (`title` kaçış kapısı vardı ama "status tags
              never truncate" artık AÇIK gereksinim); 256px bu etiketi HİÇBİR dilde kırpmadan
              barındırır. Yeni bölüşüm: Tip %13, Taşeron/Saha %18 (%31 toplam) + Geçerlilik
              144px + Durum 256px + Personel 88px (488px sabit) — `min-w-[960px]` tablo tabanı
              (floor'da Başlık ~174px). 960px hem 1366'da (main 1046px) HEM 1280'de (main tam
              960px, sıfır kaydırma) tabloyu tam oturtur; ≥1600'de (1600/1920/2560'ta ölçüldü)
              sütunlar yalnız GENİŞLER.
              **S36 #16a — Tip kendi kuralını (satır 605-606) İHLAL ediyordu.** Genişleme
              yönü DOĞRU (`%` konteynerle büyür) ama YÖN TERS: izin tipi adları Durum gibi
              SINIRLI bir katalogdan gelir (`/permit-types`, canlı ölçüm — en uzun "Kapalı
              Alanda Çalışma İzni" `text-sm`de 170px), `%13` bu içeriği yalnız table ≥1554px
              olunca barındırırdı — 960 TABANINDA (124.8px) HER ZAMAN, 1366'da (136px) HÂLÂ
              kırpardı; "~1450px" yalnız fark edildiği genişlik, TEK kırılma noktası değil.
              Tip'in kendi kuralıyla (`%` DEĞİL, sabit px) 210px'e sabitlendi (170 + 32px
              dolgu + ~8px pay). Bedelsiz değil: Başlık'ın 960 tabanındaki payı ~174px'ten
              ~93px'e düşer — `line-clamp-2` + `carbon:h-auto` (aşağıda) bunu hâlâ okunur
              tutar, tablo yine kaymaz, Personel yine görünür (960/1280/1366/1450/1600/1920'de
              canlı doğrulandı).
              **S33-8 review fix #6 — Başlık 2 satıra sarabilir**, `truncate` DEĞİL
              `line-clamp-2` (`TableCell`'in `truncate={false}` kaçış kapısı, bkz. `Table.tsx`
              dosya başlığı) — satır bunu barındırmak için `carbon:h-auto`+`carbon:min-h-[…]` ile
              BÜYÜYEBİLİR (kısa başlıklı satırlar yine 40px'te kalır); diğer hücreler tek satır
              `truncate` + `title` ile kaldı. */}
          <Table
            caption={t('permits.title')}
            className="table-fixed min-w-[960px]"
            density={density}
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-[210px]">{t('permits.list.col.permitType')}</TableHead>
                <TableHead>{t('permits.list.col.title')}</TableHead>
                <TableHead className="w-[18%]">{t('permits.list.col.contractorSites')}</TableHead>
                <TableHead className="w-[144px]">{t('permits.list.col.validity')}</TableHead>
                <TableHead className="w-[256px]">{t('permits.list.col.status')}</TableHead>
                <TableHead className="w-[88px] text-right">
                  {t('permits.list.col.personnel')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={6} />
              ) : query.isError ? (
                <TableMessageRow colSpan={6}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={6}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('permits.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={ClipboardCheck}
                      title={t('permits.list.empty')}
                      description={t('permits.list.empty.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={() => setCreateOpen(true)}>
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('permits.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((permit) => (
                  // `carbon:h-auto`+`carbon:min-h-[…]` (S33-8 review fix #6) — başlık hücresi
                  // 2 satıra SARARSA satır büyüyebilsin; kısa başlıklı satırlar `min-h` ile
                  // yine standart 40px'te kalır.
                  <TableRow key={permit.uuid} className="carbon:h-auto carbon:min-h-[var(--row-h)]">
                    <TableCell
                      className="truncate text-muted"
                      title={permit.permitTypeName ?? undefined}
                    >
                      {permit.permitTypeName ?? '—'}
                    </TableCell>
                    <TableCell truncate={false} className="carbon:py-2">
                      {/* Satırın birincil "aç" eylemi (S33 Slice 4 QA fix, defekt #4) — eskiden
                          ayrı bir "Detay →" düğmesiydi, şimdi başlığın kendisi bağlantı. Bu
                          sayfada ⋮ menüsüne taşınacak ikincil/yıkıcı bir aksiyon YOK (silme/
                          düzenleme burada değil, izin detayında) — Document Types'ın "ad =
                          bağlantı, ayrı Actions sütunu yok" emsaliyle aynı.
                          `line-clamp-2` + `title` (S33-8 review fix #6 — eskiden tek satır
                          `truncate`) — uzun başlık artık 2 satıra sarar, kalanı native tooltip
                          ile (`title`, link zaten bir `<a>`). */}
                      <TableLink
                        to={`/permits/${permit.uuid}`}
                        state={backState}
                        title={permit.title}
                        className="line-clamp-2"
                      >
                        {permit.title}
                      </TableLink>
                      {/* Owner review B1 item 1 — tek hazard biçemi: outline `Tag` (eskiden
                          burada kenarlıksız satır-içi gösterge vardı). */}
                      <HazardTag hazardClass={permit.hazardClass} className="mt-1" />
                    </TableCell>
                    <TableCell truncate={false} className="text-muted carbon:py-2">
                      <ContractorAndSites
                        contractorName={permit.contractorName}
                        sites={permit.sites}
                      />
                    </TableCell>
                    {/* `ValidityRange` (S34-12 QA fix A1) — iki satır (başlangıç / "– bitiş"),
                        `title` iki tam tarihi verir (satır sarılsa da kaybolmaz). */}
                    <TableCell
                      truncate={false}
                      className="text-muted tnum carbon:py-2"
                      title={`${formatDate(permit.validityStart, culture)} – ${formatDate(permit.validityEnd, culture)}`}
                    >
                      <ValidityRange
                        start={permit.validityStart}
                        end={permit.validityEnd}
                        culture={culture}
                      />
                    </TableCell>
                    <TableCell>
                      {/* `title` (S33-8 review fix #6, genişletilmiş — owner review B2 item 1)
                          — 256px sütunu artık ÜÇ dilin de en uzun etiketini (DE'nin 31 karakterlik
                          "Warten auf …" varyantları dahil, bkz. Table dosya başı notu) HİÇBİRİNİ
                          kırpmadan barındırır; `truncate` yine de (varsayılan) KALIYOR — yalnız
                          yarın eklenecek daha uzun, öngörülemeyen bir statü metni için savunma
                          hattı, `title` her ihtimalde tam metni taşır. */}
                      <Tag
                        color={getPermitStatusPresentation(permit.status).tagColor}
                        title={t(getPermitStatusPresentation(permit.status).labelKey)}
                      >
                        {t(getPermitStatusPresentation(permit.status).labelKey)}
                      </Tag>
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {permit.personnelCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('permits.pagination.nav'),
                previous: t('permits.pagination.previous'),
                next: t('permits.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <PermitFormModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
